import { Fragment, useEffect, useRef, useState, type CSSProperties } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CapabilityStatus, CharacterAsset, CharacterIdPhoto, CharacterPose, CharacterReason, CharacterState, PetOrigin, PetPrivateSummary } from "@/shared/contracts";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { toApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { Button, avatarChain, useAvatarStep } from "@/shared/ui";
import { PortraitStepView } from "@/features/pets/PetPortrait";
import "./id-photo.css";

/**
 * TA 的专属世界形象（CR-PLAYER-CHARACTER-01，契约 `GET /pets/{pet_id}/character`）。
 * 上传照片后自动排队（用户 2026-09-23 决定不再逐次询问授权）；这里只读状态，正常路径没有任何“生成”或“确认”按钮。
 * - `active` 与 `candidate` 分开：新版本在建或失败时，旧的生效形象照常显示。
 * - `absent` 是“还没有任务”，不转圈；`unknown` 是“结果还没确认”，不写成没生成、不自动重发。
 * - 只把实际验证过 alpha 的姿态图放进场景；按 `content_box` 与 `anchor` 落位，不按画布尺寸摆。
 */

/**
 * 能放进场景的姿态图：alpha 实测可用，且不透明占比不在两端——接近 1 基本是背景没抠掉的方图，
 * 接近 0 可能主体被裁没了（阈值只是前端兜底，发布质量由后端校验负责）。
 */
function usable(asset: CharacterAsset): boolean {
  const box = asset.content_box;
  return asset.has_alpha && asset.opaque_ratio > 0.02 && asset.opaque_ratio < 0.95 && asset.width > 0 && asset.height > 0 && box.bottom > box.top && box.right > box.left;
}

/**
 * 这一套形象里能放进场景的某个姿态图；没有这个姿态就是 null，**不拿别的姿态顶替**。
 * L2-0（合同第 6 节第 3 条）：TA 在睡觉而这一套没有睡姿时，不许把站姿当睡姿画——由 PetFigure 改用本人肖像加“在睡觉”。
 */
export function sceneAsset(state: CharacterState | null, pose: CharacterPose = "neutral_full"): CharacterAsset | null {
  const assets = state && state.active?.pet_id === state.pet_id ? state.active.assets.filter(usable) : [];
  return assets.find((asset) => asset.pose === pose) ?? null;
}

/**
 * 同一套形象里各姿态按中性站姿的身体高度定比例（与 A 约定的渲染规则）：
 * 容器高度 = 中性站姿主体高度，其他姿态用同一像素比例画——趴着睡的不会被放大成“站着那么高”。
 * 这一套里没有可用的中性站姿时，才按这张图自己的主体高度撑满。
 */
export function referenceBodyHeight(state: CharacterState | null, asset: CharacterAsset): number {
  const neutral = state?.active?.assets.find((a) => a.pose === "neutral_full" && usable(a));
  const box = (neutral ?? asset).content_box;
  return box.bottom - box.top;
}

/**
 * 后端 `blocked_reason` / `candidate.reason` / 调整结果 `reason` 给的是原因码。下面对契约 `CharacterReason` 穷举
 * （漏一个就编译不过）；字段本身仍是 string，表外的新码显示泛化说法，**绝不把原始码显示给主人**。
 * 先写成普通对象再赋给 Record：契约增减 `already_queued` 这类码时，多出的键不报错、缺的键照样报错。
 */
const REASON_COPY = {
  // 后端还在核对这一次的付费依据：玩家看到的不说「生成依据」「待核」，只说还在核对、过一会儿再看（UI-BASE-01b，W-21；原来「生成依据待核，暂时无法准备形象。」）
  charge_basis_pending: "准备形象前还在核对，过一会儿再来看看。",
  // 排队前就挡住（consent_missing / consent_revoked 已随“不再询问授权”从契约删除）
  no_reference_photo: "还没有 TA 的照片，暂时没法准备形象。",
  pet_has_no_household: "TA 还没有加入家庭，暂时没法准备形象。",
  species_unsupported: "这个物种的形象暂时还画不了。",
  provider_unavailable: "形象服务暂时不可用，稍后再看看。",
  not_configured: "形象服务暂时不可用，稍后再看看。",
  // 生图服务取回的不是 PNG（例如 Seedream 回 JPEG），没法校验、合成，发出之前就不发、不花钱；主人解决不了，后端不给重画（I / A 2026-09-27）。
  // 先于契约加进来：多出的键不报错，契约重生后正好补上 Record 缺的那一条
  provider_no_png: "现在的图片服务暂时画不了形象。",
  // 这只宠物的照片能力关着（capability_off），或读不到这项策略、按不允许（capability_unknown）（A 加码，I 2026-09-30 派）：
  // 玩家看到的是同一句泛化说法——不提是谁关的、不露码，也不许诺什么时候能画、去哪儿开；按钮照旧只听 can_regenerate。先于契约加进来，同 provider_no_png
  capability_off: "TA 的形象暂时画不了。",
  capability_unknown: "TA 的形象暂时画不了。",
  // 执行中途停下
  // 只有生成的形象照（没有主人上传的真实照片）：这里是不知道页面情况时的说法，不许诺上传；按页面实情分的说法见 generatedReferenceText
  generated_reference_only: "TA 现在用的是按描述画出来的样子。",
  // 随仓形象照来源待核的居民（这次不画；“来源待核”只给运营看，I 2026-09-30）：玩家看到的和上一条一模一样，分支见 isGeneratedReferenceOnly
  reference_source_pending: "TA 现在用的是按描述画出来的样子。",
  // 排上之后照片又换了：这一轮作废，后面也没有更新的一轮——正常换照片每次都开新一轮，只有换照片那一刻排不了新一轮
  // （自动开关关着、生图服务不可用、新照片不满足排队前提）才露出来，所以不许诺“会按新照片来”（I 2026-09-26 答复）；要重画由主人点
  reference_changed: "照片换过了，这一版没用上。",
  // 排上之后 TA 在星球上的身体换了一版（例如万能医生恢复了部位）：这一版作废、不发布；要不要按新身体重画由主人点“调整形象”，
  // 不许诺自动重画（用户 09-27 定 b；A 的 CARE-0 §8.6＋BODY-1，2026-09-28）。先于契约加进来，同 provider_no_png
  body_changed: "TA 的身体档案更新了，这一版没用上。",
  // 画出来了，但发布前的收边处理（去白边）后没过检查，这一版没用上；不自动重画，要重画由主人点“调整形象”，按钮照旧只听 can_regenerate
  // （A 的 FRINGE-1，2026-10-03）。不提“收边”“检查”这些内部说法。先于契约加进来，同 provider_no_png
  fringe_output_invalid: "这一版没画好，没有用上。",
  // 要画全身（星球形象）时身体档案里还有部位不知道，停下不画；证件照不会这样停。页面上还没有补身体信息的入口（BODY-0 §11.4），不许诺去哪儿补
  body_info_needed: "要画 TA 的全身，身体档案还缺一些信息。",
  budget_denied: "今天准备形象的次数用完了，明天再看看。",
  daily_cap: "今天准备形象的次数用完了，明天再看看。",
  rejected: "这次图片服务没有接下这个请求，没有画成。",
  timeout: "这次等了太久没有回音，结果还没确认。",
  unconfirmed: "上一次的结果还没确认。",
  unknown_result: "上一次的结果还没确认。",
  // Seedream 下它的意思是“发之前就停下了”（切换供应商前就排上的旧任务才会碰到），原来说“背景没能去干净”不贴切，改中性说法（I 2026-09-27）
  transparency_not_requested: "这一版没画成，没有用上。",
  attempts_exhausted: "试了几次都没有通过检查。",
  // 画出来了但不能用（图像校验）
  not_png: "这一版图片格式有问题，没有用上。",
  undecodable: "这一版图片格式有问题，没有用上。",
  interlaced_unsupported: "这一版图片格式有问题，没有用上。",
  palette_unsupported: "这一版图片格式有问题，没有用上。",
  bit_depth_unsupported: "这一版图片格式有问题，没有用上。",
  too_large_to_check: "这一版图片格式有问题，没有用上。",
  no_alpha_channel: "这一版背景没能去干净，没有用上。",
  opaque_background: "这一版背景没能去干净，没有用上。",
  // 透明背景在上游被压平成灰白格子（validate.py）；对玩家就是“背景没去干净”
  checkerboard_drawn: "这一版背景没能去干净，没有用上。",
  // 画像服务回的图带着背景、没去掉（I108 进契约，CONTRACT-SYNC-I108）：不是主人的错，照 opaque_background 那句；
  // 证件照那一句没有“形象”二字、和这句一字不差，分段表（ID_PHOTO_SAID）里已经有了
  background_not_removed: "这一版背景没能去干净，没有用上。",
  // 换成纯色底抠图以后的两个码（A-2 的 A2-CR31-CHROMA，I-GEN-SYNC-02 进契约；I 10-13 定）：都不是主人的错，不叫主人换照片，归「再试一次」那一类。
  // 后端不自动重画（NO_RETRY），要重画由主人点“调整形象”／“重新准备 TA 的形象”，按钮照旧只听 can_regenerate，所以这里只说这一版怎么了、不许诺再画。
  // 画出来的纯色底不够均匀、抠不干净（chroma.py）：对玩家就是背景不够干净
  key_background_not_uniform: "这一版背景不够干净，没有用上。",
  // 抠完边上留了一圈半透明的光晕（validate.py）
  background_halo: "这一版边缘有一圈光晕，没有用上。",
  empty_subject: "这一版没画出 TA，没有用上。",
  multiple_subjects: "这一版画里不止 TA 一个，没有用上。",
  subject_cut_off: "这一版把 TA 画得不完整，没有用上。",
  subject_too_large: "这一版 TA 的大小不合适，没有用上。",
  subject_too_small: "这一版 TA 的大小不合适，没有用上。",
  // 证件照自己的校验（CR-6C2B-IDPHOTO，validate.py / id_photo.py）；和形象共用这张码表
  photo_not_to_bottom: "这一版证件照没拍成头和上半身，没有用上。",
  photo_headroom_off: "这一版证件照头顶留得太挤或太空，没有用上。",
  photo_head_cut_off: "这一版证件照 TA 的头太靠边，做头像会被裁掉，没有用上。",
  photo_too_small: "这一版证件照尺寸太小，没有用上。",
  photo_wrong_shape: "这一版证件照比例不对，没有用上。",
  photo_blank: "这一版证件照是空白的，没有用上。",
  // 调整形象时已有一版在排队/生成（`web_character/service.py` regenerate）
  already_queued: "新的形象已经在准备了。",
};
const REASON_TEXT: Record<CharacterReason, string> = REASON_COPY;

/**
 * 按“只有生成的形象照”说的原因码：generated_reference_only，以及 reference_source_pending（随仓形象照来源待核的居民，这次不画；
 * “来源待核”只给运营看，玩家看到的和前者一模一样，I 2026-09-30）。页面上凡是按这种情况分支的地方都走这个判断，不各自比字符串。
 */
export function isGeneratedReferenceOnly(code: string | null | undefined): boolean {
  return code === "generated_reference_only" || code === "reference_source_pending";
}

/**
 * 说原因时页面上的实情：这只宠物的来历，以及页面上是不是真有放一张真实照片的入口（`canAddPhoto`：形象页的“补一张照片”，
 * 或已有生成的形象照时的“换一张照片”，2026-09-25 起）。
 */
export type ReferenceContext = { origin?: PetOrigin | null; canAddPhoto?: boolean };

/**
 * `generated_reference_only`（只有生成的形象照，服务端不拿它当身份源去画）按实情说，不留死胡同（2026-09-25 巡检 P1）：
 * - 页面上真有放真实照片的入口（补一张 / 换一张）时，才提补真实照片；
 * - 领养来的伙伴：TA 在星球上的样子就是这张，不要求上传（后端对领养的伙伴本来就回 not_own_pet）；
 * - 自己的宠物（或不知道来历）、页面上没有那个入口（小窝面板等）：只说现在用的是按描述画出来的样子，不许诺上传。
 * 三句都不用“形象”二字：证件照一栏也用这几句，那里的原因说法一律不出现“形象”（tests/claude-6c2b-character.test.tsx 钉着）。
 */
export function generatedReferenceText(context: ReferenceContext = {}): string {
  if (context.canAddPhoto) return "现在只有按描述画出来的样子；在上面补一张 TA 的真实照片，就能照着画。";
  if (context.origin && context.origin !== "own_pet") return "TA 在星球上的样子就是这张，不用另外上传照片。";
  return REASON_COPY.generated_reference_only;
}

/** 换过照片以后，正在展示的这一套形象是照着换掉之前那张照片画的（契约 `CharacterSet.outdated`）。形象不自动重画，由主人自己点。 */
export const CHARACTER_OUTDATED_NOTE = "星球上的 TA 是照着之前那张照片画的。";
/**
 * 证件照旁边那一句（`CharacterIdPhoto.outdated`）。2026-09-26 起照片一变后端就自动排一轮证件照，新的画好前这张仍是旧的。
 * 新一轮的进度看 `pending_status`：为 null（没有新一轮，或服务没配、根本不排）时就说这一句，不猜在不在画；
 * 新一轮没画成、没确认时，这一句跟在原因后面说（见 IdPhotoAdjust）。不带“形象”二字（证件照一栏的规矩）。
 */
export const ID_PHOTO_OUTDATED_NOTE = "证件照还是照着旧照片的。";
/**
 * 旧证件照还在、比它新的一轮（`pending_status`）在排、在画：展示的仍是旧那张，就这么说；不转圈、不给按钮，10 秒一次只读复查。
 * 只有在排、在画（queued / running）才说“正在画”。
 * 新一轮没画成（failed）先说原因（`pending_reason`，照 idPhotoReasonText 说人话，不露码；`reference_changed`＝这一轮作废、后面没有更新的一轮，
 * 说“照片换过了，这一版没用上。”），没原因时说这一句；
 * 结果没确认（unknown）一律说后一句、不带原因——可能已经发出去了，不给按钮，免得主人再付一次。后端把可能已经发出的那一轮报成 unknown 时，
 * 原因还是它停下时记的那个（例如 rejected 是“没有画成”），照原因说就成了“没画成”（I 2026-09-26：unknown 不能说成没画成）。
 */
export const ID_PHOTO_PENDING_DRAWING_NOTE = "新证件照正在画，画好前先用这张旧的。";
export const ID_PHOTO_PENDING_FAILED_NOTE = "新证件照这次没画成。";
export const ID_PHOTO_PENDING_UNKNOWN_NOTE = "新证件照的结果还没确认，先别重复点。";
/**
 * 证件照在排、在画（id_photo.status 为 queued / running）：不转圈，证件照那块先放 TA 现在的照片
 * （后端给的用法 `status === "ready" ? url : photo_url`），说“证件照正在画”；拿不到现在的照片时只说正在画。
 */
export const ID_PHOTO_DRAWING_NOTE = "证件照正在画。";
export const ID_PHOTO_DRAWING_WITH_PHOTO_NOTE = "证件照正在画，先用 TA 现在的照片。";

/**
 * 证件照那一窄栏的说法按意思切成几段（全站“说法段”的做法，同地图面板的 headlineParts）。左边放着 60px 的小证件照时，
 * 右边这一栏最窄（280 宽的形象页）只有 142px，12px 的字一行放 11 个字；原来正常断行，会断在词中间、最后一行只剩一两个字
 * （2026-09-26 巡检 R12-S2：320 宽“新证件照正在画，画好前先用这张 / 旧的。”）。
 * 这里给这一栏可能出现的每一句标好段（“|”分开，拼起来就是原文）：显示时段与段之间放 <wbr>，段里有空格的整段是一个行内块
 * （段里的“TA”不和前后的字拆开；段首段尾的空格照常可断），id-photo.css 的 .ps-idphoto-adjust__said 用 keep-all 让它只在段与段之间折行。
 * 每段不超过 11 个字（字母、空格也按一个字算），最窄那一栏也放得下，overflow-wrap 只是兜底。
 * 码表里每个原因说成证件照的那句、证件照自己的每一句都要在这里（tests/claude-6c2b-character.test.tsx 逐句、逐个状态核）；
 * 表里没有的句子照原样显示、正常断行。试排数字见 id-photo.css。
 */
const ID_PHOTO_SAID = [
  // 证件照自己的几句
  "新证件照正在画，|画好前|先用这张旧的。",
  "新证件照|这次没画成。",
  "新证件照的结果|还没确认，|先别重复点。",
  "证件照正在画。",
  "证件照正在画，|先用 |TA 现在的照片。",
  "上一次证件照的结果|还没确认，|先别重复点。",
  "证件照|这次没画成。",
  "证件照先用 |TA 现在的照片。",
  "证件照|还是照着旧照片的。",
  "还没有证件照：|护照、|居民证、|驾照都会用它。",
  "形象正在准备，|好了以后|再来画证件照。",
  "形象正在准备，|好了以后|再来重画证件照。",
  "现在还画不了|证件照。",
  "现在还不能|重画证件照。",
  "证件照|已经在准备了。",
  "这次没有排上。",
  // 码表（REASON_COPY）说成证件照的那句（idPhotoReasonText），含只有生成照片时按实情分的三句、表外新码的泛化那句
  "准备证件照前|还在核对，|过一会儿|再来看看。",
  "还没有 TA 的照片，|暂时没法|准备证件照。",
  "TA 还没有加入家庭，|暂时没法|准备证件照。",
  "这个物种的证件照|暂时还画不了。",
  "证件照服务|暂时不可用，|稍后再看看。",
  "现在的图片服务|暂时画不了|证件照。",
  // 照片能力关着、读不到策略（capability_off / capability_unknown）共用的那句
  "TA 的证件照|暂时画不了。",
  "TA 现在用的是|按描述画出来的样子。",
  "现在只有|按描述画出来的样子；|在上面补一张 |TA 的真实照片，|就能照着画。",
  "TA 在星球上的样子|就是这张，|不用另外上传照片。",
  "照片换过了，|这一版没用上。",
  "TA 的身体档案|更新了，|这一版没用上。",
  // 只有星球形象（全身）会因它停下，证件照上不会出现；码表每个码都要有证件照的说法，照样分好段
  "要画 TA 的全身，|身体档案|还缺一些信息。",
  "今天准备证件照的次数|用完了，|明天再看看。",
  "这次图片服务|没有接下这个请求，|没有画成。",
  "这次等了太久|没有回音，|结果还没确认。",
  "上一次的结果|还没确认。",
  "这一版背景|没能去干净，|没有用上。",
  "这一版背景|不够干净，|没有用上。",
  "这一版边缘|有一圈光晕，|没有用上。",
  "这一版|没画成，|没有用上。",
  "这一版|没画好，|没有用上。",
  "试了几次|都没有通过检查。",
  "这一版图片格式|有问题，|没有用上。",
  "这一版没画出 TA，|没有用上。",
  "这一版画里|不止 TA 一个，|没有用上。",
  "这一版|把 TA 画得不完整，|没有用上。",
  "这一版 |TA 的大小不合适，|没有用上。",
  "这一版证件照|没拍成头和上半身，|没有用上。",
  "这一版证件照|头顶留得太挤或太空，|没有用上。",
  "这一版证件照 |TA 的头太靠边，|做头像会被裁掉，|没有用上。",
  "这一版证件照|尺寸太小，|没有用上。",
  "这一版证件照|比例不对，|没有用上。",
  "这一版证件照|是空白的，|没有用上。",
  "新的证件照|已经在准备了。",
  "这一次|没有准备好。",
];
const ID_PHOTO_PARTS: ReadonlyMap<string, readonly string[]> = new Map(ID_PHOTO_SAID.map((said) => [said.replaceAll("|", ""), said.split("|")]));

/** 证件照那一栏的这一句按意思分成的段（拼起来就是原文）；表里没有时为 null（照原样显示）。 */
export function idPhotoPhrases(text: string): readonly string[] | null {
  return ID_PHOTO_PARTS.get(text) ?? null;
}

/**
 * 一段。段里有空格的（都挨着“TA”）整段放进一个行内块（.ps-idphoto-adjust__keep）：放得下就整段不拆，不会断成“TA / 的照片”；
 * 字放大、比这一栏还宽时才在块里折行，不撑出这一栏。不用不断行空格：文字内容保持原文（按原文取字的用例、巡检脚本都照旧）。
 * 段首、段尾的空格留在块外（那里本来就是折行处）。
 */
function Phrase({ part }: { part: string }) {
  const core = part.trim();
  if (!core.includes(" ")) return <>{part}</>;
  const start = part.indexOf(core);
  return (
    <>
      {part.slice(0, start)}
      <span className="ps-idphoto-adjust__keep">{core}</span>
      {part.slice(start + core.length)}
    </>
  );
}

/**
 * 证件照那一栏的一句：表里分好段的，段与段之间放 <wbr>（段里有空格的整段是行内块，见 Phrase），加 .ps-idphoto-adjust__said
 * 只在段与段之间折行；表里没有的照原样。文字内容就是原文；Chrome 无障碍树里是同一段里相邻的几段文字，
 * 不多出空格或换行（2026-09-26 实测）。
 */
function IdPhotoNote({ text, role, testId }: { text: string; role?: "status" | "alert"; testId?: string }) {
  const parts = idPhotoPhrases(text);
  if (!parts) {
    return (
      <p className="ps-character-adjust__note" role={role} data-testid={testId}>
        {text}
      </p>
    );
  }
  return (
    <p className="ps-character-adjust__note ps-idphoto-adjust__said" role={role} data-testid={testId}>
      {parts.map((part, i) => (
        <Fragment key={i}>
          {i > 0 ? <wbr /> : null}
          <Phrase part={part} />
        </Fragment>
      ))}
    </p>
  );
}

export function reasonText(code: string | null | undefined, context?: ReferenceContext): string | null {
  if (!code) return null;
  if (isGeneratedReferenceOnly(code)) return generatedReferenceText(context);
  return Object.prototype.hasOwnProperty.call(REASON_TEXT, code) ? (REASON_TEXT as Record<string, string>)[code] : "这一次没有准备好。";
}

/**
 * 宽栏（“调整形象”一节自己的说明）只在标点、空格处断行（home.css 的 keep-all），“TA”两边的空格就成了常用断点：
 * “TA”和紧跟的“的”之间换成不断行空格，免得断成“补一张 TA / 的真实照片”（2026-09-25 主窗口，照驾校的写法）。
 * 只在宽栏显示时换：这些原文和证件照那一窄栏共用，窄栏照常断行，原文不改。
 */
export function keepTaWithDe(text: string): string {
  return text.replaceAll("TA 的", "TA\u00a0的");
}

/**
 * 没有可放进场景的形象时，写给主人的真实状态（小窝的气泡、“陪 TA 待一会儿”面板的副标题都用它）。
 * 只有生成的形象照（generated_reference_only）时不挂“还没有星球形象”：领养来的伙伴在星球上的样子就是那张形象照，
 * 说“还没有”和形象页的“TA 在星球上的样子就是这张”打架；这里不知道来历，自己的宠物也一并不挂——场景里用的本来就是那张，
 * 要不要补真实照片由形象页按来历说（2026-09-25 主窗口）。
 */
export function characterNote(state: CharacterState | null): string | null {
  if (!state || sceneAsset(state)) return null;
  if (state.status === "queued" || state.status === "running") return "正在准备 TA 的形象";
  if (state.status === "failed") return "形象这次没画成";
  if (state.status === "unknown") return "形象结果还没确认";
  if (state.status === "absent") return state.blocked_reason && !isGeneratedReferenceOnly(state.blocked_reason) ? "还没有星球形象" : null;
  return null;
}

/**
 * 新的一版形象结果没确认（`candidate.status === "unknown"`）时，“调整形象”一节一律说这一句，不照原因说（2026-09-26 主窗口，同证件照）：
 * 后端把可能已经发出的那一版报成 unknown 时，原因还是它停下时记的那个（例如 rejected 是“没有画成”），照着说就成了“没画成”。
 * 点名“形象”：同一张卡里证件照那一节也可能说“上一次证件照的结果还没确认”，两句要分得清。
 * 不说“先别重复点”：这时后端给的 can_regenerate 仍可能为 true，按钮照样在——主窗口 2026-09-26 定了按钮保留（形象只有主人点才画、
 * 没有自动重试，也不跟着照片自动重画，收掉就成了死路；后端照旧一次点击最多一次尝试、幂等键、额度预占、每只宠物有上限）。
 * 按钮在时后面换行接一句 CHARACTER_UNCONFIRMED_AGAIN_NOTE，说清点了会怎样。
 */
export const CHARACTER_UNCONFIRMED_NOTE = "上一次形象的结果还没确认。";
/**
 * 新的一版结果没确认、这一节又给着按钮（能力开着、can_regenerate）时，接在 CHARACTER_UNCONFIRMED_NOTE 后面说的后果（照手账图 DS5 的写法）。
 * 两句之间换行（窄栏规矩：两件事写两句，每句压到最窄那一栏一行放得下——280 宽形象页的宽栏 214px、12px 字 17 个以内）。
 * 这一节不显示今天的画图次数（契约 CharacterState 里没有这个数），所以不说“会再用一次今天的画图次数”。按钮不出现时只说前一句。
 */
export const CHARACTER_UNCONFIRMED_AGAIN_NOTE = "再点会重新画一次。";

/**
 * “调整形象”一节此刻照哪个原因码说（形象页顶上那句、换照片那张卡判断“只有生成的形象照”也照它，两处取法一致）：
 * 新的一版在排、在画、结果没确认时不照原因说（分别说“新的形象正在准备。”和 CHARACTER_UNCONFIRMED_NOTE）；
 * 其余取 `candidate.reason ?? blocked_reason`。
 */
export function characterReasonCode(state: CharacterState): string | null {
  const status = state.candidate?.status;
  if (status === "queued" || status === "running" || status === "unknown") return null;
  return state.candidate?.reason ?? state.blocked_reason;
}

/** 能力表里这一项此刻的状态；演示模式、还没读到、读失败、表里没有这一项都是 null。 */
function useCapabilityStatus(key: string): CapabilityStatus | null {
  const { platform } = useServices();
  const meta = useQuery({ queryKey: queryKeys.meta, queryFn: () => platform.meta(), staleTime: 60_000, enabled: env.dataMode === "live" });
  return meta.data?.capabilities.find((capability) => capability.key === key)?.status ?? null;
}

function useCapability(key: string): boolean {
  return useCapabilityStatus(key) === "available";
}

/**
 * 能力表说这个环境画不出来（`not_configured`：没配生成，c84a D2116-4，P 的 W5 走查）时形象那一节说的这一句：
 * 不照原因说“服务暂时不可用，稍后再看看”（那会叫人等），也不说“还没有照片”（补了照片也画不了），不给按钮。
 */
export const CHARACTER_NOT_DRAWABLE_NOTE = "现在还画不了 TA 的形象。";

/** 画不出来时，这几个原因码说的都是“服务暂时不行、稍后再看”，不再照说；由“现在还不能……”那一句说清（证件照一栏用）。 */
const PROVIDER_REASONS: ReadonlySet<string> = new Set(["provider_unavailable", "not_configured", "provider_no_png"]);
function unlessNotDrawable(code: string | null | undefined, notDrawable: boolean): string | null {
  return notDrawable && code && PROVIDER_REASONS.has(code) ? null : (code ?? null);
}

/**
 * 形象查询多久复查一次（只发 GET，绝不替主人发起任何生成）：
 * - 证件照在排、在画（照片一变后端自动排的那一张，2026-09-26）→ 10 秒，画好了尽快换上；
 *   旧证件照还在、比它新的一轮在排、在画（`pending_status` 为 queued / running）→ 同样 10 秒；
 * - 整套形象在排、在画 → 20 秒；
 * - 都不是（ready / failed / unknown / absent，新一轮没画成 / 没确认 / 没有，或还没读到）→ 不复查。
 * 离开页面时这条查询没人看了，react-query 自己停；页面切到后台也不查（refetchIntervalInBackground 默认关）。
 */
export function characterPollInterval(state: CharacterState | null | undefined): number | false {
  const idPhoto = state?.id_photo?.status;
  // 新一轮只在有生效证件照时才算（契约：只有这时才给；和 IdPhotoAdjust 同一个口径）
  const pending = idPhoto === "ready" ? state?.id_photo?.pending_status : null;
  if (idPhoto === "queued" || idPhoto === "running" || pending === "queued" || pending === "running") return 10_000;
  if (state?.status === "queued" || state?.status === "running") return 20_000;
  return false;
}

/** 纯读；接口未上线（meta 报 not_implemented）时不发请求，页面退回原照肖像。有在建任务时有界只读复查（characterPollInterval）。 */
export function usePetCharacter(petId: string | null | undefined): CharacterState | null {
  const { pets } = useServices();
  const userId = useOptionalCurrentHousehold()?.userId ?? null;
  const available = useCapability("character.state");
  const state = useQuery({
    queryKey: queryKeys.characterFor(userId ?? "-", petId ?? "-"),
    queryFn: ({ signal }) => pets.character(petId!, signal),
    enabled: env.dataMode === "live" && available && Boolean(petId),
    staleTime: 15_000,
    retry: false,
    refetchInterval: (query) => characterPollInterval(query.state.data),
  });
  return state.data ?? null;
}

/**
 * 宠物面板里的形象说明与可选入口。原因只显示玩家说法；“没开生成照片许可”给去家庭设置的路。
 * 入口只在接口可用且服务端允许（`can_regenerate`）时出现；网络回执不确定时保留同一幂等键（走请求头）。
 * 能力表说这个环境画不出来（not_configured）时只说 CHARACTER_NOT_DRAWABLE_NOTE，不照原因说（W5）。
 * 注意：新账号第一只宠物上传时家庭许可必然还是关着的，后端不会自动排队——这是当前产品顺序的真实状态，
 * 这里不伪造 queued，只如实说明并在许可打开后提供可选入口。
 * 换过照片以后（2026-09-25）：正在展示的这一套是照着换掉之前那张照片画的（`active.outdated`），就如实说一句；
 * 形象后端不自动重画（2026-09-26 起自动重画的只有证件照，见 IdPhotoAdjust），这里也不替主人点——重画就是上面那颗按钮，
 * 出不出现照旧按 can_regenerate。新的一版在画时它仍为 true，照样说。
 * 新的一版结果没确认（unknown）：一律说“上一次形象的结果还没确认。”，不照原因说成“没有画成”（CHARACTER_UNCONFIRMED_NOTE、characterReasonCode）；
 * 按钮照旧按 can_regenerate 出现（主窗口 2026-09-26 定：保留），出现时换行接一句“再点会重新画一次。”（CHARACTER_UNCONFIRMED_AGAIN_NOTE）。
 */
/** “调整形象”的确认（I 10-08，2531 复核原话）：说清会整套重画、样子可能不一样；不提钱和额度 */
export const CHARACTER_REDRAW_CONFIRM = "会重新画 TA 的样子，样子可能和现在不一样；全部动作也会一起重画，需要一点时间。";

export function AdjustCharacter({ petId, state, origin = null, canAddPhoto = false, showOutdatedNote = true }: {
  petId: string;
  state: CharacterState;
  /** 这只宠物的来历（知道时传上）：原因说法按它分，见 generatedReferenceText */
  origin?: PetOrigin | null;
  /** 页面上是不是真有放一张真实照片的入口（形象页的补一张 / 换一张才可能有）：有才提补照片 */
  canAddPhoto?: boolean;
  /** 形象是照着之前那张照片画的（outdated）时这里说不说那一句。形象页把它挪到站姿图旁边说（巡检 R6-18），这里就不再说第二遍 */
  showOutdatedNote?: boolean;
}) {
  const { pets } = useServices();
  const queryClient = useQueryClient();
  const userId = useOptionalCurrentHousehold()?.userId ?? null;
  const regenerate = useCapabilityStatus("character.regenerate");
  const available = regenerate === "available";
  const notDrawable = regenerate === "not_configured";
  const keyRef = useRef(newIdempotencyKey("character-regenerate"));
  const adjust = useMutation({
    mutationFn: () => pets.regenerateCharacter(petId, { pose: "neutral_full", note: null }, keyRef.current),
    onSuccess: () => {
      keyRef.current = newIdempotencyKey("character-regenerate");
      void queryClient.invalidateQueries({ queryKey: queryKeys.characterFor(userId ?? "-", petId) });
    },
  });
  const pending = state.candidate?.status === "queued" || state.candidate?.status === "running";
  const unconfirmed = state.candidate?.status === "unknown";
  const context: ReferenceContext = { origin, canAddPhoto };
  const why = pending ? "新的形象正在准备。" : unconfirmed ? CHARACTER_UNCONFIRMED_NOTE : notDrawable ? CHARACTER_NOT_DRAWABLE_NOTE : reasonText(characterReasonCode(state), context);
  const label = state.active ? "调整形象" : state.status === "failed" || state.status === "unknown" ? "重新准备 TA 的形象" : "为 TA 准备形象";
  // 按钮出不出现：能力开着、服务端允许
  const canAdjust = available && state.can_regenerate;
  // 已经有一版形象时，点“调整形象”先确认一次（会整套重画）；还没有形象时（为 TA 准备形象）照旧直接发
  const [confirming, setConfirming] = useState(false);
  // 焦点：确认出来时落在“重新画”上；点“先不了”回到“调整形象”（Button 不收 ref，从外层容器找）
  const rootRef = useRef<HTMLDivElement | null>(null);
  const focusIn = (selector: string) => rootRef.current?.querySelector<HTMLButtonElement>(selector)?.focus();
  useEffect(() => {
    if (confirming) focusIn("[data-redraw-go]");
  }, [confirming]);
  const start = () => {
    if (state.active) setConfirming(true);
    else adjust.mutate();
  };
  return (
    <div ref={rootRef} className="ps-character-adjust" data-testid="character-adjust">
      {/* 这几句是宽栏：“TA 的”之间用不断行空格（keepTaWithDe） */}
      {showOutdatedNote && state.active?.outdated ? <p className="ps-character-adjust__note">{keepTaWithDe(CHARACTER_OUTDATED_NOTE)}</p> : null}
      {why ? (
        <p className="ps-character-adjust__note">
          {keepTaWithDe(why)}
          {/* 结果没确认、按钮又在：换行说清再点会怎样（两句，每句一行放得下） */}
          {unconfirmed && canAdjust ? (
            <>
              <br />
              {CHARACTER_UNCONFIRMED_AGAIN_NOTE}
            </>
          ) : null}
        </p>
      ) : null}
      {canAdjust && !confirming ? (
        <Button data-redraw-trigger="" variant="ghost" size="sm" loading={adjust.isPending} onClick={start}>{label}</Button>
      ) : null}
      {canAdjust && confirming ? (
        <>
          <p className="ps-character-adjust__note" id={`character-redraw-${petId}`}>{keepTaWithDe(CHARACTER_REDRAW_CONFIRM)}</p>
          <div className="ps-row" role="group" aria-labelledby={`character-redraw-${petId}`} data-testid="character-redraw-confirm">
            <Button data-redraw-go="" variant="primary" size="sm" onClick={() => { setConfirming(false); adjust.mutate(); }}>重新画</Button>
            <Button variant="ghost" size="sm" onClick={() => { setConfirming(false); requestAnimationFrame(() => focusIn("[data-redraw-trigger]")); }}>先不了</Button>
          </div>
        </>
      ) : null}
      {adjust.data && !adjust.data.accepted ? <p className="ps-character-adjust__note" role="status">{keepTaWithDe(reasonText(adjust.data.reason, context) ?? "这次没有排上。")}</p> : null}
      {adjust.isError ? <p className="ps-character-adjust__note" role="alert">{keepTaWithDe(`提交结果还没确认：${toApiError(adjust.error).playerMessage} 再点一次会沿用同一次请求，不会重复扣费。`)}</p> : null}
      <IdPhotoAdjust petId={petId} idPhoto={state.id_photo} canRequest={state.can_regenerate === true} blockedReason={state.blocked_reason} characterBusy={pending} referenceContext={context} />
    </div>
  );
}

/**
 * 证件照的原因用证件照的说法：和形象共用一张码表（CharacterReason），这里把“形象”换成“证件照”。
 * 只有生成的照片（generated_reference_only）那几句本来就不带“形象”二字，也不需要换字，照原样按实情说。
 */
export function idPhotoReasonText(code: string | null | undefined, context?: ReferenceContext): string | null {
  if (code === "already_queued") return "新的证件照已经在准备了。";
  if (isGeneratedReferenceOnly(code)) return generatedReferenceText(context);
  return reasonText(code)?.replaceAll("形象", "证件照") ?? null;
}

/**
 * 证件照（CR-6C2B-IDPHOTO）：护照、居民证、驾照都用这一张。
 * - 用户定了存量宠物不批量补，老宠物只能在这里拿到第一张：没有生效的证件照时写“画证件照”，已有画好的才写“重画证件照”。
 *   （原来写“生成证件照”；“生成”算 AI 字样，6c2b 10-14 判，UI2-POLISH-111-01 改“画证件照”，和“重画证件照”成对）
 * - 在排、在画时不给按钮；结果还没确认（unknown）时也不给——那可能已经发出去了，再点会让主人再付一次。
 * - 用的是 TA 现有照片（companion_portrait：没有真实照片的宠物）时，后端会如实拒绝重画，这里不给按钮。
 * - 能不能发起听服务端的 `can_regenerate`（上层传进来的 canRequest）：没有原照、物种画不了、没有家、图片服务不可用时它是 false，
 *   后端证件照入口（web_character/id_photo.py 的 request_in）挡的正是这一组前置条件，点了必然被拒——不给这种死路按钮，
 *   按 `blocked_reason` 用人话说为什么；没有照片的宠物就写“还没有 TA 的照片”，不承诺补传照片的入口。
 *   形象（整套角色）在排、在画时 can_regenerate 也是 false、blocked_reason 为空，证件照入口其实不看它：这里偏保守，说“等形象好了再来”、不给按钮。
 * - 照片一变（第一次补、只有生成形象照时补上真实照片、真实照片再换一张），后端自动排一次证件照（用户 2026-09-26 拍板）；
 *   这里只读、复查（usePetCharacter 在排、在画时每 10 秒一次 GET），绝不替主人发起：
 *   - 在排、在画（queued / running）：不转圈，那块先放 TA 现在的照片（家庭里这只的 photo_url，和页面顶上头像同一张），
 *     说“证件照正在画”；拿不到现在的照片（没有家庭上下文）就只说正在画；
 *   - 已有旧证件照、换了照片（`outdated`，后端只给已生效、source=generated 的那张标）：新的画好前展示的仍是旧那张。
 *     比它新的一轮看 `pending_status`（契约 2026-09-26，只增）：
 *     只有在排、在画 → “新证件照正在画，画好前先用这张旧的。”，不给按钮，10 秒一次只读复查；
 *     没画成（failed）→ 先用 `pending_reason` 说原因（idPhotoReasonText，不露码），再说“证件照还是照着旧照片的”，“重画证件照”照上面的规则出现；
 *     其中 `reference_changed`（最新那一轮本身作废、后面没有更新的一轮，只在换照片那一刻排不了新一轮时出现，I 2026-09-26）
 *     说“照片换过了，这一版没用上。”，不许诺会照新照片画——这时 outdated 也是 true，要重画由主人点“重画证件照”；
 *     没确认（unknown）→ 一律说“新证件照的结果还没确认，先别重复点。”（不带原因，见 ID_PHOTO_PENDING_UNKNOWN_NOTE）、再说还是旧的，
 *     不给按钮（可能已经发出去了）；
 *     为 null（没有新一轮，或服务没配、根本不排）→ 只说“证件照还是照着旧照片的”，不猜在不在画，按钮照上面的规则
 *     （服务没配时后端回原因）。
 * - 这一栏窄（左边有小证件照时 280 宽只剩 142px）：每一句都按意思分段显示（IdPhotoNote、ID_PHOTO_SAID），只在段与段之间折行。
 * 入口只在 meta 报 `character.id_photo` 可用时出现；网络回执不确定时沿用同一个幂等键。
 * meta 报 not_configured（这个环境画不出来）时这一节照样出现、不给按钮，说“现在还画不了证件照。”或“现在还不能重画证件照。”（W5）。
 */
export function IdPhotoAdjust({ petId, idPhoto, canRequest, blockedReason = null, characterBusy = false, referenceContext }: {
  petId: string;
  idPhoto: CharacterIdPhoto | null | undefined;
  /** 服务端此刻允不允许发起：`CharacterState.can_regenerate === true` */
  canRequest: boolean;
  /** 不允许时服务端给的原因：`CharacterState.blocked_reason` */
  blockedReason?: string | null;
  /** 形象（整套角色）正在排或在画——can_regenerate 因此为 false 时，用来说明为什么这会儿不给按钮 */
  characterBusy?: boolean;
  /** 来历与页面上有没有放真实照片的入口（补一张 / 换一张）：只有生成形象照时的说法按它分 */
  referenceContext?: ReferenceContext;
}) {
  const { pets } = useServices();
  const queryClient = useQueryClient();
  const household = useOptionalCurrentHousehold();
  const userId = household?.userId ?? null;
  const capability = useCapabilityStatus("character.id_photo");
  const keyRef = useRef(newIdempotencyKey("id-photo"));
  const request = useMutation({
    mutationFn: () => pets.regenerateIdPhoto(petId, keyRef.current),
    onSuccess: () => {
      keyRef.current = newIdempotencyKey("id-photo");
      void queryClient.invalidateQueries({ queryKey: queryKeys.characterFor(userId ?? "-", petId) });
    },
  });
  // 能力表说这个环境画不出来（not_configured）：这一节照样出现——已有的证件照照常显示、在画的照常说——只是不给按钮，
  // 说“现在还画不了证件照。”或“现在还不能重画证件照。”，原因里“服务暂时不可用，稍后再看看”那类不再照说（W5，c84a D2116-4）。其余不是 available 的照旧整节不出现。
  const notDrawable = capability === "not_configured";
  if (capability !== "available" && !notDrawable) return null;
  const status = idPhoto?.status ?? "absent";
  const photoUrl = status === "ready" ? idPhoto?.url ?? null : null;
  const generated = Boolean(photoUrl) && idPhoto?.source === "generated";
  const ownPhoto = Boolean(photoUrl) && idPhoto?.source === "companion_portrait";
  const busy = status === "queued" || status === "running";
  // 在排、在画：先放 TA 现在的照片（status === "ready" ? url : photo_url），不转圈
  const currentPhoto = busy ? (household?.pets.find(({ pet }) => pet.pet_id === petId)?.pet.photo_url ?? null) : null;
  // 旧证件照还在时比它新的一轮（pending_status）：只跟着展示着的那张说；没画成的原因照 idPhotoReasonText 说人话，不露码；
  // 没确认的不带原因（那个原因是它停下时记的，照着说会说成没画成）
  const pending = photoUrl ? (idPhoto?.pending_status ?? null) : null;
  const pendingBusy = pending === "queued" || pending === "running";
  const pendingNote = pendingBusy
    ? ID_PHOTO_PENDING_DRAWING_NOTE
    : pending === "failed"
      ? idPhotoReasonText(unlessNotDrawable(idPhoto?.pending_reason, notDrawable), referenceContext) ?? ID_PHOTO_PENDING_FAILED_NOTE
      : pending === "unknown"
        ? ID_PHOTO_PENDING_UNKNOWN_NOTE
        : null;
  const note = busy
    ? (currentPhoto ? ID_PHOTO_DRAWING_WITH_PHOTO_NOTE : ID_PHOTO_DRAWING_NOTE)
    : status === "unknown"
      ? "上一次证件照的结果还没确认，先别重复点。"
      : status === "failed"
        ? idPhotoReasonText(unlessNotDrawable(idPhoto?.reason, notDrawable), referenceContext) ?? "证件照这次没画成。"
        : ownPhoto
          ? "证件照先用 TA 现在的照片。"
          : photoUrl
            ? (idPhoto?.outdated && !pendingBusy ? ID_PHOTO_OUTDATED_NOTE : null)
            : "还没有证件照：护照、居民证、驾照都会用它。";
  // 证件照自己不在忙、结果已确认、也不是用 TA 现在的照片、比它新的一轮也不在排 / 在画 / 没确认：本来可以发起，再看服务端此刻允不允许。
  const eligible = !busy && status !== "unknown" && !ownPhoto && !pendingBusy && pending !== "unknown";
  const canAsk = eligible && canRequest && !notDrawable;
  const verb = generated ? "重画" : "画";
  const blocked = eligible && notDrawable
    ? (generated ? "现在还不能重画证件照。" : "现在还画不了证件照。")
    : eligible && !canRequest
      ? idPhotoReasonText(blockedReason, referenceContext) ?? (characterBusy ? `形象正在准备，好了以后再来${verb}证件照。` : generated ? "现在还不能重画证件照。" : "现在还画不了证件照。")
      : null;
  return (
    <div className="ps-idphoto-adjust" data-testid="id-photo-adjust">
      {photoUrl ? (
        <img className="ps-idphoto-adjust__photo" src={photoUrl} alt="TA 的证件照" />
      ) : currentPhoto ? (
        <img className="ps-idphoto-adjust__photo" src={currentPhoto} alt="TA 现在的照片" />
      ) : null}
      {/* 这一栏窄：每句按意思分段显示（IdPhotoNote） */}
      <div className="ps-idphoto-adjust__body">
        {pendingNote ? <IdPhotoNote text={pendingNote} testId="id-photo-pending" /> : null}
        {note ? <IdPhotoNote text={note} /> : null}
        {blocked ? <IdPhotoNote text={blocked} testId="id-photo-blocked" /> : null}
        {canAsk ? (
          <Button variant="ghost" size="sm" loading={request.isPending} onClick={() => request.mutate()}>
            {generated ? "重画证件照" : "画证件照"}
          </Button>
        ) : null}
        {request.data?.accepted ? <IdPhotoNote text="证件照已经在准备了。" role="status" /> : null}
        {request.data && !request.data.accepted ? <IdPhotoNote text={idPhotoReasonText(request.data.reason, referenceContext) ?? "这次没有排上。"} role="status" /> : null}
        {/* 提交没确认那句夹着一句原话（没法事先分段）：原话照常断行（.ps-idphoto-adjust__free），前后两截按意思分段 */}
        {request.isError ? (
          <p className="ps-character-adjust__note ps-idphoto-adjust__said" role="alert">
            提交结果还没确认：<wbr />
            <span className="ps-idphoto-adjust__free">{toApiError(request.error).playerMessage}</span>
            {" 再点一次"}
            <wbr />
            会沿用同一次请求，<wbr />
            不会重复扣费。
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * 小窝身份卡里 TA 的样子（还没有星球形象时）。不用名字首字（用户 2026-09-24），和全站头像同一条退回链（shared/ui 的 avatarChain，2026-09-27 D4 第三批）：
 * 照片 → 演示小灰猫（只在演示模式）→ 物种插画（只给领养来的原创居民，按快照里这只的来历与物种认）或爪印；照片打不开就往下退，不留破图。
 * 照片、演示小灰猫铺满卡片；插画、爪印按纸面写（身份卡昼夜都是暖纸，home.css 的 .ps-living-pet__identity），深色下不是纸上的深色圆块。
 * 底下小字（13px）：一律写 TA 的名字——这张卡说的是“TA 在这儿”；插画、爪印也写名字，不写“暂无照片”（I 10-03 清单第 4 件：
 * 原来 9px 的“暂无照片”挂在小窝正中，看着像报错）。照片打不开时照旧写“照片暂时打不开”（TA 其实有照片）。
 * 形象真在排、在画（preparing：真实 queued / running）时名字下面再写一行“样子还在画”；没在画就不写，不许诺。
 */
export const IDENTITY_DRAWING_NOTE = "样子还在画";
function IdentityPortrait({ pet, preparing = false }: { pet: PetPrivateSummary; preparing?: boolean }) {
  const { step, photoFailed, next } = useAvatarStep(avatarChain(pet.photo_url, pet.origin, pet.species));
  const pic = step.kind === "photo" || step.kind === "demo" ? step.src : null;
  const caption = photoFailed && step.kind !== "photo" ? "照片暂时打不开" : pet.name;
  return (
    <span className={`ps-living-pet__identity${pic ? " has-photo" : ""}`}>
      {pic ? <img key={pic} src={pic} alt="" onError={next} /> : <PortraitStepView step={step} name={pet.name} photoFailed={photoFailed} size={56} surface="paper" onError={next} />}
      <small>{caption}</small>
      {preparing && !photoFailed ? <small className="ps-living-pet__drawing">{IDENTITY_DRAWING_NOTE}</small> : null}
    </span>
  );
}

/**
 * 小窝场景里那张形象图（HOME-FIGURE-APPEAR-01，I 10-09 派；6796 BODY-2D 复核三处没过的同一根因：img 按姿势换 key 重挂，出场每次重放）。
 * 出场的模糊提亮（home.css 的 ps-figure-appear，0.9 秒）只给两种时候：这一处第一次亮相，和真换了一版（PetFigure 的 version 变了，key 跟着换、重挂）。
 * - 只换姿势：key 不变，还是这一个 img，只换 src 和落位，出场不重放；
 * - 这一版在这一处已经亮过相（图真的加载出来过）：因为姿势没图换成肖像、再换回来时，新挂上的这张标 quiet，直接出现（home.css 里 quiet 不放动画）。
 * 标记在挂上那一刻定、之后不改：放到一半从 reveal 改成 quiet 会把正在放的出场掐断。减少动态时 home.css 照旧整条停掉。
 */
function FigureImage({ reveal, src, alt, style, onLoad, onError }: { reveal: boolean; src: string; alt: string; style: CSSProperties; onLoad: () => void; onError: () => void }) {
  const [appear] = useState<"reveal" | "quiet">(reveal ? "reveal" : "quiet");
  return <img src={src} onError={onError} onLoad={onLoad} alt={alt} style={style} data-appear={appear} />;
}

/**
 * 小窝场景里的 TA。`pose` 由 HomeScene 按 L2-0 的 `life.activity.kind` 给（sleeping → 睡姿，其余中性站姿）。
 * - 要睡姿而这一套没有（或还没有形象）：画本人肖像——**不拿站姿冒充睡姿**（L2-0 合同第 6 节第 3 条）。身下不再写“在睡觉”：
 *   TA 头顶的状态牌已经说着“{名字}在屋里睡觉”，身下不再重复（I 10-14 夜间实机评审，UI2-REVIEW110-01；同“刚到家”那条）。
 * - `said`：HomeScene 另给的一句活动（TA 刚到家时“TA 刚到家”），同样写在身下。
 * 身下这一句说的是 TA 此刻在做什么（只在后端给了 life 时才有），不是 2026-09-24 撤掉的“形象状态小牌”（那个仍不挂）。
 * - 出场（HOME-FIGURE-APPEAR-01）：形象图的模糊提亮只在这一处第一次亮相、真换了一版时放；只换姿势还是同一个 img（见 FigureImage）。
 */
export function PetFigure({ pet, state, pose = "neutral_full", said = null, onUnavailable, onAvailable }: { pet: PetPrivateSummary; state: CharacterState | null; pose?: CharacterPose; said?: string | null; onUnavailable?: () => void; onAvailable?: () => void }) {
  const own = state?.pet_id === pet.pet_id && (!state.active || state.active.pet_id === pet.pet_id) ? state : null;
  const proposed = sceneAsset(own, pose);
  // 失败只属于当前资源身份的一轮；同URL的新版本也要获得自己的加载机会。
  // 这是失败那一轮的签名（带姿势、带这张图），不再拿它当 img 的 key：原来按它换 key，只换姿势也整张重挂、0.9 秒的出场每次重放（HOME-FIGURE-APPEAR-01）。
  const signature = JSON.stringify([pet.pet_id, own?.active?.character_set_id, own?.active?.revision,
    own?.active?.reference_version, own?.active?.style_version, pose,
    proposed?.asset_id, proposed?.content_sha256, proposed?.url]);
  // img 的 key：这是哪只的哪一版（哪一套、revision、reference_version、style_version）。不带姿势，也不带这张图的地址、指纹——
  // 只换姿势、同一版的图换了地址，都还是这一个 img（只换 src 和落位）；真换了一版才换 key、重挂、重放出场。
  const version = JSON.stringify([pet.pet_id, own?.active?.character_set_id, own?.active?.revision, own?.active?.reference_version, own?.active?.style_version]);
  const [round, setRound] = useState(() => ({ signature, failed: false }));
  let current = round;
  if (current.signature !== signature) {
    current = { signature, failed: false };
    setRound(current);
  }
  const owner = current;
  const ownerRef = useRef(owner);
  ownerRef.current = owner;
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  // 这一处已经亮过相的那一版（图真的加载出来才算，加载失败的不算）：因为姿势没图换成肖像、再换回来时，这一版不再模糊提亮
  const shown = useRef<string | null>(null);
  const asset = current.failed ? null : proposed;
  const line = said;
  const lifeNote = line ? <span className="ps-pet-figure__note" data-testid="pet-life-note">{line}</span> : null;
  if (asset && state?.active) {
    const round = (value: number) => Math.round(value * 100) / 100;
    // 中性站姿的主体高度正好占满容器；落地点对准容器底边中点。
    const heightPct = round((asset.height / referenceBodyHeight(state, asset)) * 100);
    return (
      <span className="ps-pet-figure is-character" data-testid="pet-character" data-revision={state.active.revision} data-anchor-measured={asset.anchor.measured ? "true" : "false"}>
        <span className="ps-pet-figure__shadow" aria-hidden="true" />
        <FigureImage
          key={version}
          reveal={shown.current !== version}
          src={asset.url}
          onError={() => {
            if (!mounted.current || ownerRef.current !== owner) return;
            const failed = { ...owner, failed: true };
            ownerRef.current = failed;
            setRound((now) => now === owner ? failed : now);
            onUnavailable?.();
          }}
          onLoad={() => {
            if (!mounted.current || ownerRef.current !== owner) return;
            // 图真的加载出来了：这一版在这一处算亮过相（见 FigureImage）
            shown.current = version;
            onAvailable?.();
          }}
          alt={`${pet.name}的星球形象`}
          style={{ height: `${heightPct}%`, aspectRatio: `${asset.width} / ${asset.height}`, transform: `translate(${round(-asset.anchor.x * 100)}%, ${round((1 - asset.anchor.y) * 100)}%)` }}
        />
        {lifeNote}
      </span>
    );
  }
  // 形象在准备时（真实 queued/running）才有光晕与光点；absent/failed/unknown 不放任何“在忙”的动效。
  // 场景里 TA 身下不再挂状态小牌（2026-09-24 巡检：小窝里一直挂着“还没有星球形象”）。PetFigure 只画在小窝场景里（HomeScene）；
  // 状态照样说得出：TA 的无障碍名称、点 TA 冒出的气泡和“陪 TA 待一会儿”面板都带 characterNote，“TA 的形象”页（/me/look）照旧写一行。
  const preparing = own?.status === "queued" || own?.status === "running";
  return (
    <span className={`ps-pet-figure is-portrait${preparing ? " is-preparing" : ""}`}>
      {preparing ? <span className="ps-pet-figure__halo" aria-hidden="true" /> : null}
      {/* 不用名字首字（用户 2026-09-24）：有照片用照片；演示模式没照片用授权的测试小灰猫；live 没照片显示爪印占位——
          领养来的原创居民除外，用居民页同一张物种插画（2026-09-25）。照片打不开按同一条退回链往下退（IdentityPortrait）。 */}
      <IdentityPortrait pet={pet} preparing={preparing} />
      {lifeNote}
      {preparing ? (
        <span className="ps-pet-figure__magic" aria-hidden="true" data-testid="pet-character-preparing">
          <b /><b /><b /><b />
          <i /><i /><i /><i /><i />
        </span>
      ) : null}
    </span>
  );
}
